import { useState, useEffect, useRef } from "react";
import { reinviteUser } from "../services/userService";

interface ApiError {
  message?: string;
}

interface ReinviteData {
  message?: string;
}

type ResendInviteResult =
  | { success: true; message: string }
  | { success: false; message: string };

const COOLDOWN_SEC = 30;

const useResendInvite = () => {
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const intervalRef = useRef<number | null>(null);

  useEffect(() => {
    if (countdown <= 0) return;

    intervalRef.current = window.setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          window.clearInterval(intervalRef.current!);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => window.clearInterval(intervalRef.current!);
  }, [countdown]);

  const handleResendInvite = async (id: string): Promise<ResendInviteResult> => {
    setLoading(true);
    const { data, error } = await reinviteUser(id) as { data: ReinviteData | null; error: ApiError | null };
    setLoading(false);

    if (error) {
      return {
        success: false,
        message: error.message || "Failed to resend invite.",
      };
    }

    setCountdown(COOLDOWN_SEC);
    return {
      success: true,
      message: data?.message ?? "Invite sent successfully",
    };
  };

  return { handleResendInvite, loading, countdown };
};

export default useResendInvite;
